import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export interface TypedDate {
  date: Dayjs;
  hasDay: boolean;
}

// Strict parsing compares the input with the date re-formatted, so "01/01"
// only matches DD/MM, never D/M — every pattern is expanded to each mix of
// one- and two-digit day and month.
const expandDigits = (patterns: string[]): string[] =>
  patterns.flatMap((pattern) =>
    ['D', 'DD'].flatMap((day) =>
      ['M', 'MM'].map((month) =>
        pattern.replace('{D}', day).replace('{M}', month)
      )
    )
  ).filter((format, index, all) => all.indexOf(format) === index);

// Australian order: day before month, tried in strict mode, so "3/4/2027" is
// 3 April, never 4 March.
const DAY_FORMATS_WITH_YEAR = [
  ...expandDigits([
    '{D}/{M}/YYYY',
    '{D}/{M}/YY',
    '{D}-{M}-YYYY',
    '{D}-{M}-YY',
    '{D}.{M}.YYYY',
    '{D}.{M}.YY',
  ]),
  ...expandDigits(['{D} MMM YYYY', '{D} MMMM YYYY', '{D} MMM YY', '{D} MMMM YY']),
];
const DAY_FORMATS_WITHOUT_YEAR = expandDigits([
  '{D}/{M}',
  '{D}-{M}',
  '{D}.{M}',
  '{D} MMM',
  '{D} MMMM',
]);
const MONTH_FORMATS_WITH_YEAR = [
  ...expandDigits(['{M}/YYYY', '{M}/YY', '{M}-YYYY']),
  'MMM YYYY',
  'MMMM YYYY',
  'MMM YY',
  'MMMM YY',
  'MMMM, YYYY',
  'MMM, YYYY',
];
const MONTH_FORMATS_WITHOUT_YEAR = ['MMM', 'MMMM'];

// customParseFormat matches month names case-sensitively, so "mar" and "MAR"
// are brought to "Mar" before parsing.
const normaliseMonthNames = (text: string) =>
  text.replace(
    /[a-z]+/gi,
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );

const parseStrict = (text: string, formats: string[]): Dayjs | null => {
  const parsed = dayjs(text, formats, true);
  return parsed.isValid() ? parsed : null;
};

// Places a day and month in `year`, clamping to the month's last day so
// 29 Feb in a non-leap year becomes 28 Feb rather than rolling into March.
const inYear = (date: Dayjs, year: number): Dayjs => {
  const monthStart = dayjs(new Date(year, date.month(), 1));
  return monthStart.date(Math.min(date.date(), monthStart.daysInMonth()));
};

/**
 * Parses a date typed into a date field, day first (Australian order).
 *
 * Without a year, the date takes the first year that puts it on or after
 * `anchor` — the plan's start date — so on a plan starting April 2026, "1/1"
 * is 1 January 2027 and "15/6" is 15 June 2026. A month on its own
 * ("Mar 2027", "3/2027", "sep") returns a month-only date (`hasDay: false`).
 *
 * @param text - What the user typed
 * @param anchor - The earliest date a year-less entry should resolve to
 * @returns The parsed date, or null when the text isn't a recognised date
 */
export const parseTypedDate = (
  text: string,
  anchor: Dayjs
): TypedDate | null => {
  const trimmed = normaliseMonthNames(text.trim().replace(/\s+/g, ' '));
  if (!trimmed) return null;

  const dayWithYear = parseStrict(trimmed, DAY_FORMATS_WITH_YEAR);
  if (dayWithYear) return { date: dayWithYear, hasDay: true };

  // Parsed against a leap year so "29/2" is accepted before the year is set.
  const dayWithoutYear = parseStrict(
    `${trimmed} 2000`,
    DAY_FORMATS_WITHOUT_YEAR.map((format) => `${format} YYYY`)
  );
  if (dayWithoutYear) {
    const sameYear = inYear(dayWithoutYear, anchor.year());
    return {
      date: sameYear.isBefore(anchor.startOf('day'))
        ? inYear(dayWithoutYear, anchor.year() + 1)
        : sameYear,
      hasDay: true,
    };
  }

  const monthWithYear = parseStrict(trimmed, MONTH_FORMATS_WITH_YEAR);
  if (monthWithYear) {
    return { date: monthWithYear.startOf('month'), hasDay: false };
  }

  const monthWithoutYear = parseStrict(trimmed, MONTH_FORMATS_WITHOUT_YEAR);
  if (monthWithoutYear) {
    const sameYear = monthWithoutYear.year(anchor.year()).startOf('month');
    return {
      date: sameYear.isBefore(anchor.startOf('month'))
        ? sameYear.add(1, 'year')
        : sameYear,
      hasDay: false,
    };
  }

  return null;
};
